<template>
    <div class="instagram-posts">
        <div class="instagram-posts-wrapper">
            <div class="title">
                <div class="pre-title">
                    {{ data.preTitle }}
                </div>

                <ContentTitle :data="data.title" />

                <div class="instagram-user">
                    <ThButton
                        :href="data.instagramUserLink"
                        :text-size="12"
                        :type="'link'"
                    >
                        {{ data.instagramUser }}
                    </ThButton>
                </div>
            </div>

            <div class="posts">
                <ul class="list">
                    <li
                        v-for="(post, key) in data.posts"
                        :key="key"
                        class="list-item"
                    >
                        <a
                            :href="post.link"
                            rel="nofollow"
                            target="_blank"
                            aria-label="Instagram post - opens in a new tab"
                        >
                            <CmsImage
                                :data="post.image"
                                :is-eager="hasEagerImage && key < 3"
                                :sizes="getPostImageSizes(key)"
                            />
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ComponentInstagramPostsFragment } from '@/graphql/api'

import ContentTitle from '@/components/cms/component/content/ContentTitle.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import type { ImageSizes } from '@/components/getImageProps.ts'

defineProps<{
    data: ComponentInstagramPostsFragment
    hasEagerImage?: boolean
}>()

const getPostImageSizes = (index: number): ImageSizes => {
    switch (index) {
        case 0:
            return ['40vw', [768, '30vw'], [1024, '350px'], [1280, '400px']]
        case 1:
            return ['50vw', [1024, '450px']]
        case 2:
            return ['45vw', [768, '40vw'], [1024, '250px']]
        case 3:
            return ['30vw', [1280, '450px']]
        default:
            return ['100vw']
    }
}
</script>

<style lang="scss" scoped>
.instagram-posts {
    width: 100%;
    overflow: hidden;

    .instagram-posts-wrapper {
        width: 100%;
        max-width: 1600px;
        margin: 0 auto;
        padding: 40px 0;
        position: relative;

        @include min-width(1024) {
            padding: 160px 0 40px;
        }

        &::before {
            content: '';
            width: 80vw;
            height: 80vw;
            top: 130px;
            left: 0;
            display: block;
            position: absolute;
            background: $stone-2;

            @include min-width(1024) {
                width: 550px;
                height: 500px;
                top: 90px;
                left: 0;
            }

            @include min-width(1280) {
                width: 650px;
                height: 600px;
                top: 90px;
            }
        }

        &::after {
            @include min-width(1024) {
                content: '';
                width: 300px;
                height: 500px;
                top: 0;
                right: 0;
                display: block;
                position: absolute;
                background: $stone-2;
            }

            @include min-width(1600) {
                right: -250px;
                width: 500px;
            }
        }
    }

    .title {
        width: 280px;
        max-width: 100%;
        position: relative;
        padding: 0 20px;
        z-index: 2;

        @include min-width(1024) {
            padding: 0 0 0 50px;
        }

        @include min-width(1280) {
            padding: 0 0 0 100px;
        }

        :deep(.th-title) {
            color: $blue;
            font-size: 2.625rem;
            line-height: 2.625rem;
            margin: 0 0 50px;

            @include min-width(1280) {
                margin: 0;
            }
        }

        .instagram-user {
            display: flex;
            justify-content: flex-start;
        }
    }

    .posts {
        .list {
            list-style: none;
            width: 100%;
            position: relative;
            height: 100vw;

            @include min-width(768) {
                height: 80vw;
            }

            @include min-width(1024) {
                height: 600px;
            }

            .list-item {
                &:nth-child(1) {
                    top: 60px;
                    left: 0;
                    position: absolute;
                    width: 40vw;
                    height: 40vw;

                    @include min-width(768) {
                        width: 30vw;
                        height: 30vw;
                        z-index: 3;
                    }

                    @include min-width(1024) {
                        width: 350px;
                        height: 350px;
                        left: 50px;
                    }

                    @include min-width(1280) {
                        width: 400px;
                        height: 400px;
                        left: 100px;
                        top: 120px;
                    }
                }

                &:nth-child(2) {
                    top: -20px;
                    right: 0;
                    position: absolute;
                    width: 50vw;
                    height: 50vw;

                    @include min-width(768) {
                        width: 50vw;
                        height: 50vw;
                        top: -60px;
                        z-index: 3;
                    }

                    @include min-width(1024) {
                        width: 450px;
                        height: 450px;
                        top: -200px;
                        left: 50%;
                    }

                    @include min-width(1280) {
                        width: 450px;
                        height: 450px;
                        top: -220px;
                        left: 400px;
                    }
                }

                &:nth-child(3) {
                    left: 30px;
                    bottom: 20px;
                    position: absolute;
                    width: 45vw;
                    height: 45vw;
                    z-index: 3;

                    @include min-width(768) {
                        width: 40vw;
                        height: 40vw;
                    }

                    @include min-width(1024) {
                        width: 250px;
                        height: 250px;
                        bottom: 0;
                        left: 300px;
                    }

                    @include min-width(1280) {
                        bottom: 40px;
                        left: auto;
                        right: 400px;
                    }
                }

                &:nth-child(4) {
                    right: 30px;
                    bottom: 40px;
                    position: absolute;
                    width: 30vw;
                    height: 30vw;
                    z-index: 3;

                    @include min-width(1280) {
                        width: 450px;
                        height: 450px;
                        inset: -80px 40px auto auto;
                        z-index: 3;
                    }
                }
            }
        }
    }
}
</style>
